import React, { useEffect, useState } from 'react';
import { Table, Tag, Progress, Tabs, Spin, Button, Popconfirm } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ProjectsApi } from 'api/api';
import { ProjectDetailsDto, UnitCategoryDto, UnitSummaryDto, DealSummaryDto, SupplierPaymentDto } from 'api/models';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main } from 'container/styled';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { formatCurrency, formatDate, formatSquareMeters } from 'utility/utility';
import { ProjectStatisticsChart } from './components/ProjectStatisticsChart';
import styled from 'styled-components';
import ProjectDetailsHeader from './components/ProjectDetailsHeader';
import ProjectPlansTable from './components/ProjectPlansTable';
import SupplierPaymentsStatusColumn from './components/SupplierPaymentsStatusColumn';

const { TabPane } = Tabs;
const projectsApi = new ProjectsApi();

const Loader = styled.div`
  display: flex;
  height: 400px;
  width: 600px;
  justify-content: center;
  align-items: center;
`;

interface SupplierSummary {
  supplierId: number;
  supplierName: string;
  totalAmount: number;
  totalPayments: number;
  pendingPayments: number;
  paidPayments: number;
  overduePayments: number;
  latestDueDate?: string;
  partiallyPaidPayments: number;
}

const groupSupplierPayments = (payments: SupplierPaymentDto[]): SupplierSummary[] => {
  const groupedMap = payments.reduce((acc, payment) => {
    const key = payment.supplierId!;
    if (!acc.has(key)) {
      acc.set(key, {
        supplierId: payment.supplierId!,
        supplierName: payment.supplierName!,
        totalAmount: 0,
        totalPayments: 0,
        pendingPayments: 0,
        paidPayments: 0,
        overduePayments: 0,
        latestDueDate: payment.dueDate ? formatDate(payment.dueDate) : undefined,
        partiallyPaidPayments: 0,
      });
    }

    const summary = acc.get(key)!;
    summary.totalAmount += payment.amount!;
    summary.totalPayments += 1;
    summary.pendingPayments += payment.isPaid ? 0 : 1;
    summary.paidPayments += payment.isPaid ? 1 : 0;
    summary.overduePayments += payment.isOverdue ? 1 : 0;
    summary.partiallyPaidPayments += payment.isPartiallyPaid ? 1 : 0;

    // Update latest due date if this payment's due date is later
    if (payment.dueDate && (!summary.latestDueDate || new Date(payment.dueDate) > new Date(summary.latestDueDate))) {
      summary.latestDueDate = payment.dueDate;
    }

    return acc;
  }, new Map<number, SupplierSummary>());

  return Array.from(groupedMap.values());
};

export const ProjectDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState<ProjectDetailsDto | null>(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const response = await projectsApi.apiProjectsIdGet({ id: Number(id) });
        setProject(response.data);
      } catch (err) {
        openNotificationWithIcon('error', t('projects:fetch-error', 'Failed to fetch project details'));
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id, t]);

  const categoriesColumns = [
    {
      title: t('projects:category-name', 'Category'),
      dataIndex: 'name',
      render: (categoryName: string, record: UnitCategoryDto) => (
        <Link to={`/unit-categories/${record.id}`}>{categoryName}</Link>
      ),
    },
    {
      title: t('projects:total-units', 'Total Units'),
      dataIndex: 'unitsCount',
      key: 'unitsCount',
    },
    {
      title: t('projects:available-units', 'Available Units'),
      dataIndex: 'availableUnitsCount',
      key: 'availableUnitsCount',
      render: (count: number, record: UnitCategoryDto) => (
        <Progress percent={Math.round((count / (record.unitsCount || 1)) * 100)} size="small" format={() => count} />
      ),
    },
  ];

  const supplierSummaryColumns = [
    {
      title: t('suppliers:supplier-name', 'Supplier'),
      dataIndex: 'supplierName',
      key: 'supplierName',
      render: (name: string, record: SupplierSummary) => <Link to={`/suppliers/${record.supplierId}`}>{name}</Link>,
    },
    {
      title: t('suppliers:total-amount', 'Total Amount'),
      key: 'totalAmount',
      width: 150,
      render: (record: SupplierSummary) => <strong>{formatCurrency(record.totalAmount)}</strong>,
    },
    {
      title: t('suppliers:payments-summary', 'Payments'),
      key: 'payments',
      render: (record: SupplierSummary) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div>
            <Tag color="blue">
              {t('suppliers:total', 'Total')}: {record.totalPayments}
            </Tag>
            <Tag color="success">
              {t('suppliers:paid', 'Paid')}: {record.paidPayments}
            </Tag>
            <Tag color="processing">
              {t('suppliers:partially-paid', 'Partially Paid')}: {record.partiallyPaidPayments}
            </Tag>
            <Tag color="warning">
              {t('suppliers:pending', 'Pending')}: {record.pendingPayments}
            </Tag>
            {record.overduePayments > 0 && (
              <Tag color="error">
                {t('suppliers:overdue', 'Overdue')}: {record.overduePayments}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('suppliers:latest-due', 'Latest Due'),
      dataIndex: 'latestDueDate',
      key: 'latestDueDate',
      width: 150,
      render: (date: string) => formatDate(date),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 120,
      render: (record: SupplierSummary) => (
        <div className="table-actions">
          <Button
            type="primary"
            icon={<FeatherIcon icon="list" size={16} />}
            onClick={() => navigate(`/supplier-payments?supplierId=${record.supplierId}&projectId=${id}`)}
          >
            {t('suppliers:view-all-payments', 'View All')}
          </Button>
        </div>
      ),
    },
  ];

  const unitsColumns = [
    {
      title: t('projects:unit-name', 'Unit'),
      dataIndex: 'name',
      render: (unitName: string, record: UnitSummaryDto) => <Link to={`/units/${record.id}`}>{unitName}</Link>,
    },
    {
      title: t('projects:category', 'Category'),
      dataIndex: 'categoryName',
      key: 'categoryName',
    },
    {
      title: t('projects:square-meters', 'Square Meters'),
      dataIndex: 'squareMeters',
      key: 'squareMeters',
      render: (value: number) => formatSquareMeters(value),
    },
    {
      title: t('projects:stock', 'Stock'),
      dataIndex: 'stock',
      key: 'stock',
      render: (stock: number) => <Tag color={stock > 0 ? 'green' : 'red'}>{stock}</Tag>,
    },
  ];

  const dealsColumns = [
    {
      title: t('projects:client', 'Client'),
      dataIndex: 'clientName',
      render: (clientName: string, record: DealSummaryDto) => (
        <Link to={`/clients/${record.clientId}`}>{clientName}</Link>
      ),
    },
    {
      title: t('projects:units', 'Units'),
      dataIndex: 'unitNames',
      key: 'unitNames',
      render: (units: string[]) => units?.join(', '),
    },
    {
      title: t('projects:amount', 'Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: t('projects:date', 'Date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: t('projects:status', 'Status'),
      dataIndex: 'dealStatus',
      key: 'dealStatus',
      render: (status: string) => (
        <Tag color={status === 'Active' ? 'green' : 'red'}>
          {t(`projects:deal-status-${status.toLowerCase()}`, status)}
        </Tag>
      ),
    },
    {
      title: t('projects:actions', 'Actions'),
      key: 'actions',
      render: (_: any, record: DealSummaryDto) => (
        <Link to={`/deals/${record.id}`}>
          <FeatherIcon icon="eye" size={16} />
        </Link>
      ),
    },
  ];

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={project?.name}
          subTitle={
            project?.name && (
              <Tag color={project?.isActive ? 'success' : 'danger'}>
                {project?.isActive ? t('global:active', 'Active') : t('global:inactive', 'Inactive')}
              </Tag>
            )
          }
        />
      </CardToolbox>

      <Main>
        <ProjectDetailsHeader statistics={project?.statistics} loading={loading} />
        <Tabs defaultActiveKey="statistics" className="mt-4">
          <TabPane
            tab={
              <span>
                <FeatherIcon icon="bar-chart-2" size={16} />
                {t('projects:statistics', 'Statistics')}
              </span>
            }
            key="statistics"
          >
            <ProjectStatisticsChart statistics={project?.statistics} loading={loading} />
          </TabPane>

          <TabPane
            tab={
              <span>
                <FeatherIcon icon="dollar-sign" size={16} />
                {t('projects:expenses', 'Expenses')}
              </span>
            }
            key="expenses"
          >
            <Table
              loading={loading}
              dataSource={groupSupplierPayments(project?.supplierPayments || [])}
              columns={supplierSummaryColumns}
              rowKey="supplierId"
              pagination={false}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <FeatherIcon icon="grid" size={16} />
                {t('projects:categories', 'Categories')}
              </span>
            }
            key="categories"
          >
            <Table
              loading={loading}
              dataSource={project?.categories || []}
              columns={categoriesColumns}
              rowKey="id"
              pagination={false}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <FeatherIcon icon="box" size={16} />
                {t('projects:units', 'Units')}
              </span>
            }
            key="units"
          >
            <Table
              loading={loading}
              dataSource={project?.units || []}
              columns={unitsColumns}
              rowKey="id"
              pagination={false}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <FeatherIcon icon="check-circle" size={16} />
                {t('projects:active-deals', 'Active Deals')}
              </span>
            }
            key="deals"
          >
            <Table
              loading={loading}
              dataSource={project?.activeDeals || []}
              columns={dealsColumns}
              rowKey="id"
              pagination={false}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <FeatherIcon icon="clipboard" size={16} />
                {t('projects:planifications', 'Plans')}
              </span>
            }
            key="plans"
          >
            <ProjectPlansTable projectId={Number(id)} />
          </TabPane>
        </Tabs>
      </Main>
    </>
  );
};

export default ProjectDetailsPage;
